import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import clsx from 'clsx'

import { AppBuilderAPI, ProjectsAPI } from '@makyoapp/makyo-sdk'
import { ProjectStatus } from '@makyoapp/makyo-sdk/models/Projects'
import { ProjectAdapter } from '@makyoapp/makyo-common'
import { CaretDown, Db16, Design16, Redo16, Undo16 } from '@makyoapp/makyo-icon/icon'
import {
  CountryOption,
  DDItem,
  DropdownV2,
  SelectDropdown,
  SwitchSize,
  TextSwitch,
  TextSwitchVariant,
  Tooltip,
} from '@makyoapp/makyo-ui'

import { getLanguage, Language } from '@/lib/language-flag'
import { getComponentDirection } from '@/shared/builder-utils'
import { MAKYO_APP_DOMAIN, ORG_DATABASE_ROUTE, PROJECT_ROUTE } from '@/shared/constants'

export function ProjectAction({ project, onPublished }: { project: ProjectAdapter; onPublished: () => void }) {
  return (
    <div className='flex-cc'>
      <ProjectActionDropdown project={project} onPublished={onPublished} />
      <ProjectDesignDataSwitch projectId={project.id} />
    </div>
  )
}

let prevPath: string | undefined = undefined
let prevState: any = undefined

export function ProjectDesignDataSwitch({ projectId }: { projectId: string }) {
  const [currentTable, setCurrentTable] = useState<string | undefined>(undefined)
  const location = useLocation()
  const pathNameSplit = location.pathname.split('/')
  const locationState = location.state as Record<string, any>
  const navigate = useNavigate()

  const isDbRoute = useMemo(() => {
    return pathNameSplit[3] === ORG_DATABASE_ROUTE
  }, [pathNameSplit])

  const isAppRoute = useMemo(() => {
    return pathNameSplit[3] === 'app'
  }, [pathNameSplit])

  useEffect(() => {
    if (isDbRoute) {
      setCurrentTable(pathNameSplit[4])
    }
  }, [isDbRoute, pathNameSplit])

  const handleNavigate = useCallback(
    (to?: 'app' | 'db') => {
      if (to) {
        // switch from db to app builder
        if (to === 'app' && !isAppRoute) {
          let nextPath = `${PROJECT_ROUTE}/${projectId}/app`
          if (prevPath && nextPath !== prevPath) {
            nextPath = prevPath
          }
          navigate(nextPath, { replace: true, state: prevState })
          prevPath = undefined
          prevState = undefined
          return
        }

        // switch from app builder to db
        if (to === 'db' && !isDbRoute) {
          let nextPath = `${PROJECT_ROUTE}/${projectId}/${ORG_DATABASE_ROUTE}`

          if (currentTable) {
            nextPath = nextPath.concat(`/${currentTable}`)
          }
          // set last path
          prevPath = window.location.pathname
          if (locationState) {
            prevState = locationState
          }

          navigate(nextPath, { replace: true })
          return
        }

        return
      }

      if (isDbRoute) {
        // switch from db to app builder
        let nextPath = `${PROJECT_ROUTE}/${projectId}/app`
        if (prevPath && nextPath !== prevPath) {
          nextPath = prevPath
        }
        navigate(nextPath, { replace: true, state: prevState })
        prevPath = undefined
        prevState = undefined
      } else {
        // switch from app builder to db
        let nextPath = `${PROJECT_ROUTE}/${projectId}/${ORG_DATABASE_ROUTE}`

        if (currentTable) {
          nextPath = nextPath.concat(`/${currentTable}`)
        }
        // set last path
        prevPath = window.location.pathname
        if (locationState) {
          prevState = locationState
        }

        navigate(nextPath, { replace: true })
      }
    },
    [currentTable, isAppRoute, isDbRoute, locationState, navigate, projectId]
  )

  // Add keyboard event handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isCtrlOrCmd = event.ctrlKey || event.metaKey // Check for Ctrl (Windows) or Cmd (macOS)

      // if E, navigate to app builder
      if (event.key === 'e' && isCtrlOrCmd && !event.altKey && !event.shiftKey) {
        event.preventDefault()
        handleNavigate('app')
      }

      // if D, navigate to data
      if (event.key === 'd' && isCtrlOrCmd && !event.altKey && !event.shiftKey) {
        event.preventDefault()
        handleNavigate('db')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleNavigate])

  return (
    <div className='flex-cc h-full'>
      <TextSwitch
        size={SwitchSize.L}
        variant={TextSwitchVariant.Corner}
        checked={isDbRoute}
        noColor
        textOn={
          <div className='flex-c'>
            <Db16 />
            <div>
              <Trans>Data</Trans>
            </div>
          </div>
        }
        textOff={
          <div className='flex-c'>
            <Design16 />
            <div>
              <Trans>App Builder</Trans>
            </div>
          </div>
        }
        onClick={() => handleNavigate()}
      />
      {/* <div className='absolute bottom-[-20px] left-0 right-0 text-center text-light text-[10px]'>
        Press Option/Alt to switch
      </div> */}
    </div>
  )
}

export function ProjectActionDropdown({ project, onPublished }: { project: ProjectAdapter; onPublished: () => void }) {
  // ─── States ──────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false)
  const [currentProject, setCurrentProject] = useState<ProjectAdapter | null>(project)
  const appID = currentProject?.id || ''

  // ─── Functions ───────────────────────────────────────────────────────────────

  const handlePublishAction = async () => {
    if (isLoading || !currentProject || !appID) return

    setIsLoading(true)

    await AppBuilderAPI.ReqPublish(appID)
      .then(() => {
        setCurrentProject((prev) => ({ ...prev!, status: ProjectStatus.Published }))
        toast.success('App published successfully')
        onPublished?.()
      })
      .catch(() => {
        toast.error('Failed to publish app')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleUnpublishAction = async () => {
    if (isLoading || !currentProject || !appID) return

    setIsLoading(true)

    await ProjectsAPI.ReqChangeStatus(appID, ProjectStatus.Unpublished)
      .then(() => {
        setCurrentProject((prev) => ({ ...prev!, status: ProjectStatus.Unpublished }))
        toast.success('App unpublished successfully')
        onPublished?.()
      })
      .catch(() => {
        toast.error('Failed to unpublish app')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleViewPublishedApp = () => {
    if (!currentProject) return

    const verifiedDomain = currentProject.domains?.find((d) => d.verified && !d.domain.includes('localhost'))?.domain

    const domain = verifiedDomain
      ? verifiedDomain.replace(/^https?:\/\//, '')
      : `${currentProject.href}.${MAKYO_APP_DOMAIN}`

    window.open(`https://${domain}`, '_blank')
  }

  useEffect(() => {
    const handleDomainUpdated = (e: Event) => {
      const detail = (e as CustomEvent).detail
      setCurrentProject((prev) => ({ ...prev!, href: detail.domain }))
    }
    window.addEventListener('builder-domain-updated', handleDomainUpdated)
    return () => window.removeEventListener('builder-domain-updated', handleDomainUpdated)
  }, [])

  if (!currentProject) {
    return null
  }

  return (
    <DropdownV2>
      <DropdownV2.Trigger asChild>
        <div
          className={clsx(
            'rounded-sm font-semibold text-white flex items-center h-9',
            isLoading ? 'bg-slate-500' : 'bg-primary-500 hover:bg-primary-600'
          )}
        >
          <button
            id='app-publish-btn'
            disabled={isLoading}
            className='px-4 h-full'
            onClick={(e) => {
              e.stopPropagation()
              handlePublishAction()
            }}
          >
            <div className='flex flex-row gap-[10px]'>
              {isLoading && <span className='loading spin'></span>}
              <Trans>{isLoading ? 'Publishing' : 'Publish'}</Trans>
            </div>
          </button>

          <span className='w-[1px] h-full bg-slate-900' />
          <button className='flex items-center px-2 h-full'>
            <CaretDown />
          </button>
        </div>
      </DropdownV2.Trigger>

      <DropdownV2.Content showArrow className='dark w-[180px] text-m-m py-2' align='center'>
        <DDItem className='dd-menu-row px-2!' noPadX onClick={handleUnpublishAction}>
          <div className={clsx('dd-menu-item-inner')}>
            <Trans>Unpublish</Trans>
          </div>
        </DDItem>

        <DDItem className='dd-menu-row px-2!' noPadX onClick={handleViewPublishedApp}>
          <div className={clsx('dd-menu-item-inner')}>
            <Trans>View Published App</Trans>
          </div>
        </DDItem>
      </DropdownV2.Content>
    </DropdownV2>
  )
}

interface RedoUndoProps {
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
}

export function RedoUndo({ canUndo, canRedo, onUndo, onRedo }: RedoUndoProps) {
  return (
    <div className='flex h-full border-l-trans border-r-trans divide-x divide-slate-600'>
      <Tooltip content='Undo'>
        <button
          className={clsx(
            'w-12 h-full flex items-center justify-center',
            canUndo ? 'cursor-pointer opacity-100 hover:bg-slate-700' : 'cursor-not-allowed opacity-50'
          )}
          disabled={!canUndo}
          onClick={onUndo}
        >
          <Undo16 />
        </button>
      </Tooltip>

      <Tooltip content='Redo'>
        <button
          className={clsx(
            'w-12 h-full flex items-center justify-center',
            canRedo ? 'cursor-pointer opacity-100 hover:bg-slate-700' : 'cursor-not-allowed opacity-50'
          )}
          disabled={!canRedo}
          onClick={onRedo}
        >
          <Redo16 />
        </button>
      </Tooltip>
    </div>
  )
}

function LanguageFlagDropdown({
  languages,
  activeLanguage,
  onChange,
}: {
  languages: Language[]
  activeLanguage: string
  onChange: (lng: string) => void
}) {
  const value = getLanguage(activeLanguage)

  const { '*': paramValue } = useParams()
  const projectId = paramValue?.split('/')[1]

  const changeActiveLanguage = (lng: string) => {
    const localeCode = lng.slice(0, 2)
    const dir = getComponentDirection(localeCode)
    const activeLanguages = JSON.parse(localStorage.getItem(`activeLanguages:${projectId}`) || '{}')

    activeLanguages[projectId!] = lng
    localStorage.setItem(`activeLanguages:${projectId}`, JSON.stringify(activeLanguages))
    onChange(lng)
  }

  return languages.length > 1 ? (
    <div className='h-full border-r-trans'>
      <SelectDropdown
        outlined
        value={value}
        onChange={(opt, e) => {
          e.stopPropagation()
          changeActiveLanguage(opt.ids.locale)
        }}
        optionLabel={(opt) => opt.ids.locale}
        renderOption={(opt) => {
          return <CountryOption slug={opt.countryCode} name={opt.name} />
        }}
        options={languages}
      />
    </div>
  ) : null
}

export function ProjectSettingDropdown({
  languages,
  activeLanguage,
  onLanguageChange,
}: {
  languages: Language[]
  activeLanguage: string
  onLanguageChange: (lng: string) => void
}) {
  return (
    <div className='flex-c'>
      <LanguageFlagDropdown languages={languages} activeLanguage={activeLanguage} onChange={onLanguageChange} />
    </div>
  )
}
